import type { ActionOutcome, DraftAction, PublicState, Role, TableState } from '../types.ts';
import { checkSeatCommand, HANDLERS } from '../reduce.ts';
import { seatedRoles, type Ctx } from './common.ts';
import { actionStage, usesScanner } from './drafts.ts';
import { NO_SCANNER, scannerBox } from './act.ts';

/** Resolve eligible queue heads in rotating seat order. */
export function resolveStage(ctx: Ctx, t: TableState, queues: Partial<Record<Role, DraftAction[]>>): ActionOutcome[] {
  const roles = seatedRoles(t);
  const offset = (ctx.s.clock.turn - 1) % roles.length;
  const order = [...roles.slice(offset), ...roles.slice(0, offset)];
  const outcomes: ActionOutcome[] = [];
  const grants = scannerGrants(ctx, t, queues);
  const count = roles.reduce((n, role) => n + (queues[role]?.length ?? 0), 0);
  for (let pass = 0; pass < count; pass++) {
    let progressed = false;
    for (const role of order) {
      const action = queues[role]?.[0];
      if (!action) continue;
      const cmd = action.cmd;
      const paired = (cmd.type === 'OPERATE' && cmd.theatre !== 1) || (cmd.type === 'ANAESTHETISE' && cmd.theatre !== 1);
      const partnerRole = role === 'SURG' ? 'ICU' : 'SURG';
      const partner = queues[partnerRole]?.[0];
      if (paired && !(role === 'SURG' && partner?.cmd.type === 'ANAESTHETISE' && partner.cmd.theatre !== 1)) continue;
      const reason = paired && partner ? frozenIntentReason(ctx.s, action) ?? checkSeatCommand(ctx.s, t.no, role, cmd) ?? checkSeatCommand(ctx.s, t.no, partnerRole, partner.cmd) : null;
      queues[role]!.shift();
      progressed = true;
      outcomes.push(reason ? { seat: role, actionId: action.id, status: 'BLOCKED', reason } : resolveAction(ctx, t, role, action, grants.get(action.id)));
      if (!paired || !partner) continue;
      queues[partnerRole]!.shift();
      outcomes.push(reason ? { seat: partnerRole, actionId: partner.id, status: 'BLOCKED', reason } : resolveAction(ctx, t, partnerRole, partner, grants.get(partner.id)));
    }
    if (!progressed) break;
  }
  for (const role of order) {
    for (const action of queues[role] ?? []) outcomes.push({ seat: role, actionId: action.id, status: 'BLOCKED', reason: 'Theatre needs the surgeon and the anaesthetist to plan the same patient at the same point in their plans; no action was used.' });
  }
  return outcomes;
}

/** Reserve scanner boxes for this stage's scan requests, highest priority first (ties: each seat's first scan, then its second; seat order within a round). */
export function scannerGrants(ctx: Ctx, t: TableState, queues: Partial<Record<Role, DraftAction[]>>): Map<string, number> {
  const requests = seatedRoles(t).flatMap(role => (queues[role] ?? []).filter(usesScanner).map((action, rank) => ({ role, action, rank })));
  requests.sort((a, b) => (b.action.priority ?? 2) - (a.action.priority ?? 2) || a.rank - b.rank);
  const grants = new Map<string, number>();
  for (const { role, action } of requests) {
    const box = scannerBox(ctx.s, t, role);
    if (box < 0) continue;
    t.board.scannerUsedBy[box] = role;
    grants.set(action.id, box);
  }
  return grants;
}

/** Refuse changed public care rather than silently selecting another step. */
export function frozenIntentReason(pub: PublicState, action: DraftAction): string | null {
  const cmd = action.cmd;
  const intent = action.intent;
  const clinical = ['START_ROUTE', 'TREAT', 'OPERATE', 'RED_BLANKET', 'DISCHARGE', 'EARLY_DISCHARGE', 'STEP_DOWN'].includes(cmd.type)
    || (cmd.type === 'USE_LOCUM' && (cmd.action === 'TREAT' || cmd.action === 'DISCHARGE'));
  if (!clinical || !intent || !('p' in cmd) || !cmd.p) return null;
  const p = pub.patients[cmd.p];
  if (!p) return 'The selected care is no longer available.';
  const routeChanged = cmd.type !== 'START_ROUTE' && (intent.route !== (p.route?.code ?? null) || intent.step !== (p.route?.next ?? null));
  return p.believedKey !== intent.diagnosis || routeChanged ? 'The patient’s care changed before this could happen; no action was used.' : null;
}

/** Check public eligibility and execute one frozen intention; `box` is the scanner box already reserved for it. */
export function resolveAction(ctx: Ctx, t: TableState, role: Role, action: DraftAction, box?: number): ActionOutcome {
  // Free the reserved box so the ordinary check and run claim it; a scan that then fails leaves it wasted.
  if (box !== undefined) t.board.scannerUsedBy[box] = null;
  const outcome = resolveEligible(ctx, t, role, action);
  if (box !== undefined && outcome.status !== 'APPLIED') t.board.scannerUsedBy[box] = role;
  if (outcome.status === 'BLOCKED' && usesScanner(action) && outcome.reason.endsWith(NO_SCANNER)) {
    const st = t.seats[role]!;
    // A scan that could not run is owed back as a bonus action next turn.
    st.scanCredit = (st.scanCredit ?? 0) + HANDLERS[action.cmd.type].cost(ctx.s, t, role, action.cmd as never);
  }
  return outcome;
}

function resolveEligible(ctx: Ctx, t: TableState, role: Role, action: DraftAction): ActionOutcome {
  const p = 'p' in action.cmd && action.cmd.p ? ctx.s.patients[action.cmd.p] : undefined;
  const label = `${action.cmd.type.replaceAll('_', ' ')}${p ? `: ${p.name}` : ''}`;
  if (p && actionStage(action.cmd) === 'DEPARTURE' && (p.flags.dischargeNotBeforeTurn ?? 0) > ctx.s.clock.turn) {
    return { seat: role, actionId: action.id, status: 'DELAYED', reason: `${label}: held back by workload until turn ${p.flags.dischargeNotBeforeTurn}; no action used.` };
  }
  const h = HANDLERS[action.cmd.type];
  const cost = h.cost(ctx.s, t, role, action.cmd as never);
  const reason = frozenIntentReason(ctx.s, action) ?? (cost > t.seats[role]!.actions.available ? 'No actions left for this.' : h.check(ctx.s, t, role, action.cmd as never));
  if (reason) return { seat: role, actionId: action.id, status: 'BLOCKED', reason: `${label}: ${reason}` };
  h.run(ctx, t, role, action.cmd as never);
  return { seat: role, actionId: action.id, status: 'APPLIED', reason: `${label}: done.` };
}
